import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MousePointerClick } from "lucide-react";
import { HOME } from "@/constants/testIds";
import HeroAssistantWizard from "./HeroAssistantWizard";

// Row 1 of the homepage zigzag: text LEFT, 4-character grid RIGHT.
// No video embed here — the product demo lives once, at the bottom in HomeVideoDemo.

const CHARACTERS = [
  { key: "assist", name: "Zynthoro", src: "/assistants/assist.png" },
  { key: "thoro", name: "Thoro", src: "/assistants/thoro.png" },
  { key: "zyona", name: "Zyona", src: "/assistants/zyona.png" },
  { key: "zyntha", name: "Zyntha", src: "/assistants/zyntha.png" },
];

export default function Hero() {
  const [activeAssistant, setActiveAssistant] = useState(null);

  return (
    <section
      id="top"
      data-testid={HOME.hero}
      className="relative overflow-hidden zy-section"
      style={{ paddingTop: 96, paddingBottom: 96 }}
    >
      <div className="zy-hero-bg" aria-hidden="true">
        <div className="zy-hero-orb zy-hero-orb-a" />
        <div className="zy-hero-orb zy-hero-orb-b" />
      </div>

      <div className="max-w-5xl mx-auto px-6 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* LEFT — copy */}
          <div className="text-left">
            <div className="zy-badge zy-reveal" style={{ transitionDelay: "0ms" }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#202B60]" />
              Powered by Claude & Gemini
            </div>

            <p className="mt-6 text-[12px] font-bold uppercase tracking-[0.16em] text-[#202B60] zy-reveal" style={{ transitionDelay: "80ms" }}>
              Where Ambition Wins.
            </p>

            <h1
              data-testid={HOME.heroHeadline}
              className="mt-3 zy-h1 zy-reveal"
              style={{ transitionDelay: "80ms" }}
            >
              Your entire business.{" "}
              <span style={{ color: "var(--zy-blue)" }}>One platform.</span>
            </h1>

            <p
              data-testid={HOME.heroSubheadline}
              className="mt-6 zy-body max-w-lg zy-reveal"
              style={{ transitionDelay: "160ms" }}
            >
              CRM, projects, finance and marketing in one connected workspace, with four built-in AI specialists. For teams worldwide.
            </p>

            <p
              data-testid="hero-assistant-hint"
              className="mt-3 inline-flex items-center gap-2 text-[13.5px] italic text-[#0A1628]/70 zy-reveal"
              style={{ transitionDelay: "200ms" }}
            >
              <MousePointerClick size={14} className="text-[#202B60]" />
              Click any AI specialist to get a personal plan recommendation.
            </p>

            <div
              className="mt-8 flex flex-col sm:flex-row items-start gap-4 zy-reveal"
              style={{ transitionDelay: "240ms" }}
            >
              <Link
                to="/signup?trial=1"
                data-testid={HOME.heroPrimaryCta}
                className="zy-btn-primary"
              >
                Explore the 7-day demo
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/pricing#pricing"
                data-testid="hero-cta-get-started"
                className="zy-btn-outline"
              >
                Compare plans
              </Link>
            </div>

            <p className="mt-4 text-[13px] text-[#888] zy-reveal" style={{ transitionDelay: "300ms" }}>
              Fictional Business company · Four assistants · No real external actions
            </p>
          </div>

          {/* RIGHT — 2x2 character grid (clickable) */}
          <div
            data-testid="hero-character-grid"
            className="grid grid-cols-2 gap-6 zy-reveal"
            style={{ transitionDelay: "340ms" }}
          >
            {CHARACTERS.map((c) => (
              <button
                type="button"
                key={c.key}
                data-testid={`hero-character-${c.key}`}
                onClick={() => setActiveAssistant(c)}
                aria-label={`Get a personal plan recommendation from ${c.name}`}
                className="group flex flex-col items-center bg-transparent border-0 p-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#202B60] focus-visible:ring-offset-2 rounded-2xl"
              >
                <img
                  src={c.src}
                  alt={
                    c.key === "assist"
                      ? "Zynthoro Assist, always-on business guide for SMEs"
                      : c.key === "thoro"
                      ? "Thoro, Zynthoro's workflows & builder AI specialist"
                      : c.key === "zyona"
                      ? "Zyona, Zynthoro's business & growth AI specialist"
                      : "Zyntha, Zynthoro's content & SEO AI specialist"
                  }
                  loading="eager"
                  className="w-40 h-40 md:w-44 md:h-44 object-cover object-top rounded-2xl border border-[#eef1f7] shadow-sm bg-white transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_18px_44px_-18px_rgba(10,22,40,0.35)]"
                />
                <span className="text-[13px] font-medium text-[#0A1628]/70 mt-3 group-hover:text-[#0A1628] transition-colors">
                  {c.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <HeroAssistantWizard
        assistant={activeAssistant}
        onClose={() => setActiveAssistant(null)}
      />
    </section>
  );
}

